// ============================================================
// RentalQuotes.jsx  — Quotes tab for RentalDashboard
// ============================================================
import React, { useState, useEffect, useRef } from 'react';
import axios from './lib/axiosClient';
import { daysBetween } from './lib/rentalDays';
import { fmtDate } from './lib/fmtDate';
import { getAutoRateType, getAutoPrice, getLineTotal } from './lib/rentalPricing';

const fmt  = (n) => '$' + Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const STATUS_COLOR = {
  draft:           { bg:'#2e3150', color:'#64748b' },
  sent:            { bg:'#1e3a5f', color:'#60a5fa' },
  viewed:          { bg:'#1a2a3a', color:'#93c5fd' },
  accepted:        { bg:'#14532d', color:'#4ade80' },
  declined:        { bg:'#3b0a0a', color:'#f87171' },
  expired:         { bg:'#252840', color:'#4a5568' },
  pending_request: { bg:'#3b2a00', color:'#fbbf24' },
  converted:       { bg:'#1a2a1a', color:'#4a5568' },
};

// ── Mileage Settings Panel ────────────────────────────────────
function MileageSettings({ onClose }) {
  const [s, setS]     = useState({ free_miles:10, rate_per_mile:2.50, min_delivery_fee:0, business_address:'', quote_expiry_days:7, quote_notes:'', notification_email:'', business_name:'' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    axios.get(`/api/rentals/settings`)
      .then(r => setS(r.data.settings || s))
      .catch(() => {});
  }, []);

  async function save() {
    setSaving(true);
    try {
      const keys = ['free_miles','rate_per_mile','min_delivery_fee','business_address','quote_expiry_days','quote_notes','notification_email','business_name'];
      await axios.put(`/api/rentals/settings`, Object.fromEntries(keys.filter(k => s[k] !== undefined).map(k => [k, s[k]])));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch(e) { alert('Could not save settings'); }
    setSaving(false);
  }

  const inp = { padding:'8px 10px', borderRadius:6, border:'1px solid #3a3f60', background:'#252838', color:'#e2e8f0', fontSize:13, width:'100%', boxSizing:'border-box' };
  const lbl = { fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4, marginTop:12 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#252838', borderRadius:16, width:'100%', maxWidth:480, border:'1px solid #2e3150', boxShadow:'0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #2e3150', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:700, fontSize:16, color:'#e2e8f0' }}>⚙️ Quote & Delivery Settings</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:20 }}>✕</button>
        </div>
        <div style={{ padding:'0 24px 20px', maxHeight:'70vh', overflowY:'auto' }}>
          <div style={{ marginTop:16, padding:12, background:'#1e2235', borderRadius:8, border:'1px solid #2e3150' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#f97316', marginBottom:8 }}>🏢 Business Info</div>
            <label style={lbl}>Business Name</label>
            <input style={inp} value={s.business_name||''} onChange={e=>setS(p=>({...p,business_name:e.target.value}))} placeholder="Your business name" />
            <label style={lbl}>Notification Email (receives quote requests & alerts)</label>
            <input style={inp} type="email" value={s.notification_email||''} onChange={e=>setS(p=>({...p,notification_email:e.target.value}))} placeholder="owner@yourbusiness.com" />
            <div style={{ fontSize:11, color:'#4a5568', marginTop:4 }}>When a customer submits a quote request from your storefront, this is where the email goes.</div>
          </div>

          <div style={{ marginTop:16, padding:12, background:'#1e2235', borderRadius:8, border:'1px solid #2e3150' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#f97316', marginBottom:8 }}>🚚 Mileage / Delivery Tiers</div>
            <label style={lbl}>Business Address (origin)</label>
            <input style={inp} value={s.business_address||''} onChange={e=>setS(p=>({...p,business_address:e.target.value}))} placeholder="Street, City, ST ZIP" />
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={lbl}>Free Miles (round trip)</label>
                <input style={inp} type="number" min="0" value={s.free_miles} onChange={e=>setS(p=>({...p,free_miles:e.target.value}))} />
                <div style={{ fontSize:11, color:'#4a5568', marginTop:3 }}>No charge under this distance</div>
              </div>
              <div>
                <label style={lbl}>Rate per Mile (after free)</label>
                <input style={inp} type="number" min="0" step="0.25" value={s.rate_per_mile} onChange={e=>setS(p=>({...p,rate_per_mile:e.target.value}))} />
                <div style={{ fontSize:11, color:'#4a5568', marginTop:3 }}>Applied to round-trip miles</div>
              </div>
            </div>
            <div>
              <label style={lbl}>Minimum Delivery Fee</label>
              <input style={inp} type="number" min="0" step="1" value={s.min_delivery_fee||0} onChange={e=>setS(p=>({...p,min_delivery_fee:e.target.value}))} placeholder="0" />
            </div>
            <div style={{ marginTop:10, padding:10, background:'#0f1020', borderRadius:6, fontSize:12, color:'#64748b' }}>
              Example: {s.free_miles} free miles round-trip, then ${Number(s.rate_per_mile).toFixed(2)}/mi.
              A 20-mile one-way delivery = {Number(s.free_miles)*2 < 40 ? `${40 - Number(s.free_miles)*2} billable miles × $${Number(s.rate_per_mile).toFixed(2)} = $${((40 - Number(s.free_miles)*2) * Number(s.rate_per_mile)).toFixed(2)}` : 'free (within free miles)'}
            </div>
          </div>

          <div style={{ marginTop:16, padding:12, background:'#1e2235', borderRadius:8, border:'1px solid #2e3150' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#f97316', marginBottom:8 }}>📋 Quote Defaults</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={lbl}>Quote Expiry (days)</label>
                <input style={inp} type="number" min="1" value={s.quote_expiry_days||7} onChange={e=>setS(p=>({...p,quote_expiry_days:e.target.value}))} />
              </div>
              <div style={{ fontSize:11, color:'#64748b', alignSelf:'end', paddingBottom:8 }}>Tax parts and taxability are set in Settings → Tax.</div>
            </div>
            <label style={lbl}>Default Quote Notes</label>
            <textarea style={{...inp, height:80, resize:'none'}} value={s.quote_notes||''} onChange={e=>setS(p=>({...p,quote_notes:e.target.value}))} placeholder="Thank you for your interest..." />
          </div>
        </div>
        <div style={{ padding:'12px 24px', borderTop:'1px solid #2e3150', display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'9px 20px', borderRadius:8, border:'1px solid #3a3f60', background:'transparent', color:'#94a3b8', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding:'9px 20px', borderRadius:8, border:'none', background:'#f97316', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13 }}>
            {saved ? '✓ Saved!' : saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Quote Form ────────────────────────────────────────────────
function QuoteForm({ onClose, onSaved, inventoryItems, prefill = null }) {
  const [settings, setSettings] = useState({ free_miles:10, rate_per_mile:2.50, min_delivery_fee:0, quote_expiry_days:7 });
  const [form, setForm] = useState({
    client_name:      prefill?.client_name      || '',
    client_email:     prefill?.client_email     || '',
    client_phone:     prefill?.client_phone     || '',
    client_company:   prefill?.client_company   || '',
    delivery_address: prefill?.delivery_address || '',
    delivery_miles:   0,
    fulfillment_type: prefill?.fulfillment_type || 'delivery',
    from_date:        prefill?.from_date        || '',
    to_date:          prefill?.to_date          || '',
    notes:            prefill?.notes            || '',
    discount_amount:  0,
    client_id:        prefill?.client_id        || null,
    tax_exempt:       false,
    tax_exempt_reason:'',
  });
  // Client picker (for tax exemption + linking the quote to a client)
  const [clientResults, setClientResults] = useState([]);
  const [showClients, setShowClients]     = useState(false);
  useEffect(() => {
    const q = (form.client_name || '').trim();
    if (q.length < 2 || form.client_id) { setClientResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await axios.get(`/api/rentals/clients/search?q=${encodeURIComponent(q)}`); setClientResults(r.data.clients || []); }
      catch { setClientResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [form.client_name, form.client_id]);
  function pickClient(c) {
    setForm(p => ({ ...p, client_id: c.id, client_name: c.name, client_email: c.email || p.client_email, client_phone: c.phone || p.client_phone, client_company: c.company || p.client_company }));
    setShowClients(false);
  }
  const [items, setItems] = useState(
    prefill?.rental_quote_items?.map(i => ({
      product_group_id: i.product_group_id || '',
      item_name: i.item_name || '',
      quantity: i.quantity || 1,
      rate_type: 'day',
      unit_price: i.unit_price || 0,
      total_price: i.total_price || 0,
      custom_from: '', custom_to: '',
    })) || []
  );
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [distLoading, setDistLoading] = useState(false);
  const [distError,   setDistError]   = useState('');
  const distTimer = React.useRef(null);

  useEffect(() => {
    axios.get(`/api/rentals/settings`).then(r => setSettings(r.data.settings || settings)).catch(()=>{});
  }, []);

  // Auto-calculate distance when delivery address changes
  useEffect(() => {
    if (form.fulfillment_type !== 'delivery' || !form.delivery_address || form.delivery_address.length < 8) return;
    clearTimeout(distTimer.current);
    distTimer.current = setTimeout(async () => {
      setDistLoading(true); setDistError('');
      try {
        const r = await axios.get(`/api/rentals/distance?destination=${encodeURIComponent(form.delivery_address)}`);
        setForm(p => ({ ...p, delivery_miles: r.data.miles_one_way }));
      } catch(e) { setDistError(e.response?.data?.error || 'Could not calculate distance'); }
      setDistLoading(false);
    }, 900);
    return () => clearTimeout(distTimer.current);
  }, [form.delivery_address, form.fulfillment_type]);

  const days = form.from_date && form.to_date
    ? daysBetween(form.from_date, form.to_date) : 1;

  // Mileage calc
  const roundTrip = Number(form.delivery_miles) * 2;
  const freeMiles  = Number(settings.free_miles || 10);
  const ratePerMi  = Number(settings.rate_per_mile || 2.50);
  const minFee     = Number(settings.min_delivery_fee || 0);
  const mileageCharge = form.fulfillment_type === 'pickup' ? 0
    : roundTrip <= freeMiles ? minFee
    : Math.max(minFee, (roundTrip - freeMiles) * ratePerMi);

  // Totals from the server (POST /api/rentals/quotes/preview-totals —
  // the same computeTotals that POST /quotes stores). Lines are priced
  // from the catalog there; the numbers typed here are only the request.
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const previewTimer = React.useRef(null);
  const previewKey = JSON.stringify({
    from_date: form.from_date, to_date: form.to_date, fulfillment_type: form.fulfillment_type, delivery_miles: form.delivery_miles,
    discount_amount: form.discount_amount, client_id: form.client_id, tax_exempt: form.tax_exempt, tax_exempt_reason: form.tax_exempt ? form.tax_exempt_reason : '',
    items: items.map(i => ({ product_group_id: i.product_group_id || null, item_name: i.item_name, quantity: i.quantity, rate_type: i.rate_type, unit_price: i.unit_price, total_price: i.total_price })),
  });
  useEffect(() => {
    const body = JSON.parse(previewKey);
    if (!body.items.length) { setPreview(null); setPreviewError(''); return; }
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try { const r = await axios.post('/api/rentals/quotes/preview-totals', body); setPreview(r.data.totals); setPreviewError(''); }
      catch (e) { setPreview(null); setPreviewError(e.response?.data?.error || 'Could not compute totals'); }
    }, 300);
    return () => clearTimeout(previewTimer.current);
  }, [previewKey]);
  const subtotal = Number(preview?.subtotal || 0);
  const total    = Number(preview?.total || 0);
  const clientExempt = preview?.tax_exempt && preview?.tax_exempt_source === 'client';

  function addItem() {
    setItems(p => [...p, { product_group_id: '', item_name:'', quantity:1, rate_type:'day', unit_price:0, total_price:0, custom_from:'', custom_to:'' }]);
  }
  function updateItem(i, field, val) {
    setItems(p => p.map((item, idx) => {
      if (idx !== i) return item;
      const updated = { ...item, [field]: val };
      // Calculate days for this item (use item override or global)
      const fromD = updated.custom_from || form.from_date;
      const toD   = updated.custom_to   || form.to_date;
      const itemDays = fromD && toD ? daysBetween(fromD, toD) : days || 1;
      const inv = updated.product_group_id
        ? inventoryItems.find(x => String(x.id) === String(updated.product_group_id))
        : null;

      if (field === 'product_group_id' && inv) {
        // Picking a catalog item sets the auto rate for the current date
        // range — the starting point. Staff can still type over unit_price
        // afterward; that edit is their override and isn't re-derived here.
        updated.item_name  = inv.name;
        updated.rate_type  = getAutoRateType(inv, itemDays);
        updated.unit_price = getAutoPrice(inv, updated.rate_type, itemDays);
      } else if ((field === 'custom_from' || field === 'custom_to') && inv) {
        // Date range changed on a catalog item — re-derive the rate tier,
        // since a longer window can newly cross the week/month threshold.
        updated.rate_type  = getAutoRateType(inv, itemDays);
        updated.unit_price = getAutoPrice(inv, updated.rate_type, itemDays);
      }
      // Recompute the total from whatever rate_type/unit_price are set now —
      // either just auto-derived above, or exactly as staff typed them if
      // this was a direct unit_price edit (their override, left alone).
      updated.total_price = getLineTotal(updated, updated.rate_type, updated.unit_price, updated.quantity, itemDays);
      return updated;
    }));
  }

  // Recalc totals when dates change
  useEffect(() => {
    setItems(p => p.map(i => ({ ...i, total_price: Number(i.unit_price) * Number(i.quantity) * days })));
  }, [form.from_date, form.to_date]);

  async function saveQuote(andSend = false) {
    if (!form.client_name.trim()) return alert('Client name is required.');
    if (items.length === 0) return alert('Add at least one item.');
    if (form.tax_exempt && !String(form.tax_exempt_reason||'').trim()) return alert('A reason is required to mark this quote tax-exempt.');
    andSend ? setSending(true) : setSaving(true);
    try {
      const { data } = await axios.post(`/api/rentals/quotes`, { ...form, items });
      if (andSend && (form.client_email || form.client_phone)) {
        await axios.post(`/api/rentals/quotes/${data.quote.id}/send`);
      }
      if (prefill?.id) {
        await axios.put(`/api/rentals/quotes/${prefill.id}`, { status: 'converted' }).catch(()=>{});
      }
      onSaved(data.quote, andSend);
    } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    setSaving(false); setSending(false);
  }

  const inp = { padding:'8px 10px', borderRadius:6, border:'1px solid #3a3f60', background:'#252838', color:'#e2e8f0', fontSize:13, width:'100%', boxSizing:'border-box' };
  const lbl = { fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4, marginTop:12 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:3000, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:20, overflowY:'auto' }}>
      <div style={{ background:'#252838', borderRadius:16, width:'100%', maxWidth:680, border:'1px solid #2e3150', boxShadow:'0 20px 60px rgba(0,0,0,0.5)', marginTop:20 }}>
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #2e3150', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:700, fontSize:16, color:'#e2e8f0' }}>{prefill ? '📋 Build Quote from Request' : '📋 New Quote'}</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:20 }}>✕</button>
        </div>
        <div style={{ padding:'0 24px 8px', maxHeight:'75vh', overflowY:'auto' }}>

          {/* Customer Info */}
          <div style={{ marginTop:16, padding:12, background:'#1e2235', borderRadius:8, border:'1px solid #2e3150' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#f97316', marginBottom:8 }}>👤 Customer</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div style={{ gridColumn:'1/-1', position:'relative' }}>
                <label style={lbl}>Full Name *</label>
                <input style={inp} value={form.client_name} onChange={e=>{ setShowClients(true); setForm(p=>({...p,client_name:e.target.value, client_id:null})); }} onFocus={()=>setShowClients(true)} autoComplete="off" placeholder="John Smith" />

                {showClients && clientResults.length > 0 && !form.client_id && (

                  <div style={{ position:'absolute', zIndex:50, background:'#252838', border:'1px solid #3a3f60', borderRadius:8, marginTop:2, maxHeight:180, overflowY:'auto', width:'100%', boxShadow:'0 8px 24px rgba(0,0,0,0.5)' }}>

                    {clientResults.map(c => (

                      <div key={c.id} onMouseDown={()=>pickClient(c)} style={{ padding:'8px 12px', cursor:'pointer', borderBottom:'1px solid #1e2235', fontSize:12, color:'#e2e8f0' }}>

                        <div style={{ fontWeight:600 }}>{c.name}</div><div style={{ fontSize:10, color:'#94a3b8' }}>{[c.company, c.email].filter(Boolean).join(' · ')}</div>

                      </div>

                    ))}

                  </div>

                )}

                {form.client_id && <div style={{ fontSize:10, color:'#4ade80', marginTop:3 }}>✓ Linked to client #{form.client_id} <button type="button" onClick={()=>setForm(p=>({...p,client_id:null}))} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:10 }}>unlink</button></div>}
              </div>
              <div>
                <label style={lbl}>Email</label>
                <input style={inp} type="email" value={form.client_email} onChange={e=>setForm(p=>({...p,client_email:e.target.value}))} placeholder="john@example.com" />
              </div>
              <div>
                <label style={lbl}>Phone</label>
                <input style={inp} type="tel" value={form.client_phone} onChange={e=>setForm(p=>({...p,client_phone:e.target.value}))} placeholder="(702) 555-0100" />
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Company</label>
                <input style={inp} value={form.client_company} onChange={e=>setForm(p=>({...p,client_company:e.target.value}))} placeholder="Smith Construction LLC" />
              </div>
            </div>
          </div>

          {/* Rental Period */}
          <div style={{ marginTop:12, padding:12, background:'#1e2235', borderRadius:8, border:'1px solid #2e3150' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#f97316', marginBottom:8 }}>📅 Rental Period</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div>
                <label style={lbl}>Pickup / Start Date</label>
                <input style={inp} type="date" value={form.from_date} onChange={e=>setForm(p=>({...p,from_date:e.target.value}))} />
              </div>
              <div>
                <label style={lbl}>Return Date</label>
                <input style={inp} type="date" value={form.to_date} onChange={e=>setForm(p=>({...p,to_date:e.target.value}))} />
              </div>
            </div>
            {days > 0 && form.from_date && form.to_date && (
              <div style={{ marginTop:8, fontSize:12, color:'#f97316' }}>📆 {days} day{days!==1?'s':''}</div>
            )}
          </div>

          {/* Delivery */}
          <div style={{ marginTop:12, padding:12, background:'#1e2235', borderRadius:8, border:'1px solid #2e3150' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#f97316', marginBottom:8 }}>🚚 Delivery</div>
            <div style={{ display:'flex', gap:10, marginBottom:10 }}>
              {['delivery','pickup'].map(t => (
                <button key={t} onClick={()=>setForm(p=>({...p,fulfillment_type:t}))}
                  style={{ flex:1, padding:'8px', borderRadius:6, border:`1px solid ${form.fulfillment_type===t?'#f97316':'#3a3f60'}`, background:form.fulfillment_type===t?'rgba(249,115,22,0.1)':'transparent', color:form.fulfillment_type===t?'#f97316':'#64748b', fontWeight:600, fontSize:12, cursor:'pointer', textTransform:'capitalize' }}>
                  {t === 'delivery' ? '🚚 Delivery' : '🏪 Customer Pickup'}
                </button>
              ))}
            </div>
            {form.fulfillment_type === 'delivery' && (<>
              <label style={lbl}>Delivery Address</label>
              <div style={{ position:'relative' }}>
                <input style={inp} value={form.delivery_address} onChange={e=>setForm(p=>({...p,delivery_address:e.target.value}))} placeholder="1234 Job Site Ave, City, ST" />
                {distLoading && <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:11, color:'#60a5fa' }}>📍 Calculating...</span>}
              </div>
              {distError && <div style={{ fontSize:11, color:'#f87171', marginTop:3 }}>⚠️ {distError} — enter miles manually</div>}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:4 }}>
                <div>
                  <label style={lbl}>One-Way Miles {distLoading ? '(calculating...)' : form.delivery_miles > 0 ? '✓ Auto-calculated' : ''}</label>
                  <input style={{ ...inp, borderColor: form.delivery_miles > 0 && !distLoading ? '#22c55e' : undefined }} type="number" min="0" step="0.1" value={form.delivery_miles} onChange={e=>setForm(p=>({...p,delivery_miles:e.target.value}))} placeholder="0" />
                  <div style={{ fontSize:11, color:'#4a5568', marginTop:3 }}>Round trip = {(Number(form.delivery_miles)*2).toFixed(1)} mi</div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', justifyContent:'flex-end', paddingBottom:4 }}>
                  <div style={{ padding:'10px 12px', background:'#0f1020', borderRadius:8, border:'1px solid #2e3150' }}>
                    <div style={{ fontSize:11, color:'#64748b' }}>Delivery Charge</div>
                    <div style={{ fontSize:18, fontWeight:700, color: mileageCharge > 0 ? '#f97316' : '#4ade80' }}>
                      {mileageCharge > 0 ? fmt(mileageCharge) : 'FREE'}
                    </div>
                    {roundTrip <= freeMiles && roundTrip > 0 && (
                      <div style={{ fontSize:10, color:'#4ade80' }}>Within {freeMiles}-mile free zone</div>
                    )}
                    {roundTrip > freeMiles && (
                      <div style={{ fontSize:10, color:'#64748b' }}>{(roundTrip-freeMiles).toFixed(1)} billable mi × ${ratePerMi.toFixed(2)}</div>
                    )}
                  </div>
                </div>
              </div>
            </>)}
          </div>

          {/* Items */}
          <div style={{ marginTop:12, padding:12, background:'#1e2235', borderRadius:8, border:'1px solid #2e3150' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#f97316' }}>📦 Equipment</div>
              <button onClick={addItem} style={{ padding:'4px 12px', borderRadius:6, border:'none', background:'#f97316', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>+ Add Item</button>
            </div>
            {items.length === 0 && (
              <div style={{ textAlign:'center', padding:'20px 0', color:'#3a3f60', fontSize:12 }}>No items yet — click + Add Item</div>
            )}
            {items.map((item, i) => {
              const itemFrom = item.custom_from || form.from_date;
              const itemTo   = item.custom_to   || form.to_date;
              const itemDays = itemFrom && itemTo ? daysBetween(itemFrom, itemTo) : days || 1;
              return (
              <div key={i} style={{ marginBottom:12, padding:'10px 10px 6px', background:'#0f1020', borderRadius:8, border:`1px solid #2e3150` }}>
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr auto', gap:8, alignItems:'end' }}>
                  <div>
                    {i === 0 && <label style={lbl}>Item</label>}
                    <select style={inp} value={item.product_group_id} onChange={e=>updateItem(i,'product_group_id',e.target.value)}>
                      <option value="">— Select or type —</option>
                      {inventoryItems.map(inv => <option key={inv.id} value={inv.id}>{inv.name}</option>)}
                    </select>
                    {!item.product_group_id && <input style={{...inp,marginTop:4}} value={item.item_name} onChange={e=>updateItem(i,'item_name',e.target.value)} placeholder="Custom item name" />}
                  </div>
                  <div>
                    {i === 0 && <label style={lbl}>Qty</label>}
                    <input style={inp} type="number" min="1" value={item.quantity} onChange={e=>updateItem(i,'quantity',e.target.value)} />
                  </div>
                  <div>
                    {i === 0 && <label style={lbl}>Rate</label>}
                    <input style={inp} type="number" min="0" step="0.01" value={item.unit_price} onChange={e=>updateItem(i,'unit_price',e.target.value)} />
                  </div>
                  <div>
                    {i === 0 && <label style={lbl}>Subtotal</label>}
                    <div style={{ padding:'9px 10px', borderRadius:6, background:'#1e2235', border:'1px solid #2e3150', fontSize:13, color:'#f97316', fontWeight:600 }}>
                      {fmt(item.total_price)}
                    </div>
                    {(item.custom_from || item.custom_to) && <div style={{ fontSize:10, color:'#60a5fa', marginTop:2 }}>{itemDays}d custom</div>}
                    {item.rate_type && item.rate_type !== 'day' && <div style={{ fontSize:10, color:'#a78bfa', marginTop:2 }}>{item.rate_type} rate</div>}
                  </div>
                  <div>
                    {i === 0 && <div style={{ height:23 }} />}
                    <button onClick={()=>setItems(p=>p.filter((_,idx)=>idx!==i))} style={{ padding:'8px 10px', borderRadius:6, border:'none', background:'#2a1a1a', color:'#f87171', cursor:'pointer', fontSize:12 }}>✕</button>
                  </div>
                </div>
                {/* Per-item date override */}
                <div style={{ marginTop:6 }}>
                  <button type="button" onClick={()=>updateItem(i,'custom_from',item.custom_from===undefined?'':undefined)}
                    style={{ background:'none', border:'none', color: (item.custom_from||item.custom_to)?'#60a5fa':'#4a5568', fontSize:11, cursor:'pointer', padding:'2px 0' }}>
                    {(item.custom_from||item.custom_to) ? '📅 Custom dates active' : '+ Override dates for this item'}
                  </button>
                  {(item.custom_from !== undefined) && (
                    <div style={{ display:'flex', gap:8, marginTop:4, alignItems:'center' }}>
                      <input type="date" value={item.custom_from||''} onChange={e=>updateItem(i,'custom_from',e.target.value)}
                        style={{ ...inp, width:140, fontSize:11, padding:'5px 8px' }} />
                      <span style={{ color:'#4a5568', fontSize:11 }}>→</span>
                      <input type="date" value={item.custom_to||''} onChange={e=>updateItem(i,'custom_to',e.target.value)}
                        style={{ ...inp, width:140, fontSize:11, padding:'5px 8px' }} />
                      <button type="button" onClick={()=>setItems(p=>p.map((it,ix)=>ix===i?{...it,custom_from:undefined,custom_to:''}:it))}
                        style={{ background:'none', border:'none', color:'#4a5568', cursor:'pointer', fontSize:11 }}>Clear</button>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>

          {/* Notes & Discount */}
          <div style={{ marginTop:12, padding:12, background:'#1e2235', borderRadius:8, border:'1px solid #2e3150' }}>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:10 }}>
              <div>
                <label style={lbl}>Notes (visible to customer)</label>
                <textarea style={{...inp, height:70, resize:'none'}} value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Special instructions, terms..." />
              </div>
              <div>
                <label style={lbl}>Discount Amount</label>
                <input style={inp} type="number" min="0" step="0.01" value={form.discount_amount} onChange={e=>setForm(p=>({...p,discount_amount:e.target.value}))} placeholder="0.00" />
                <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#e2e8f0', marginTop:12, cursor: clientExempt ? 'not-allowed' : 'pointer' }}>
                  <input type="checkbox" checked={!!form.tax_exempt || !!clientExempt} disabled={!!clientExempt} onChange={e=>setForm(p=>({...p,tax_exempt:e.target.checked}))} style={{ accentColor:'#f97316' }} />
                  Tax exempt{clientExempt ? ' (client certificate)' : ''}
                </label>
                {form.tax_exempt && !clientExempt && (
                  <input style={{ ...inp, marginTop:6 }} placeholder="Reason (required)" value={form.tax_exempt_reason} onChange={e=>setForm(p=>({...p,tax_exempt_reason:e.target.value}))} />
                )}
              </div>
            </div>
          </div>

          {/* Total Preview */}
          <div style={{ marginTop:12, padding:14, background:'#0f1020', borderRadius:10, border:'1px solid #2e3150' }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#64748b', marginBottom:4 }}>
              <span>Equipment Subtotal</span><span>{fmt(subtotal)}</span>
            </div>
            {Number(preview?.delivery_fee) > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#64748b', marginBottom:4 }}>
                <span>Delivery ({(Number(form.delivery_miles)*2).toFixed(1)} mi round trip)</span><span>{fmt(preview.delivery_fee)}</span>
              </div>
            )}
            {Number(preview?.discount_amount) > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#4ade80', marginBottom:4 }}>
                <span>Discount</span><span>-{fmt(preview.discount_amount)}</span>
              </div>
            )}
            {preview?.tax_exempt ? (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#64748b', marginBottom:4 }}>
                <span>Tax</span><span>Exempt{preview.tax_exempt_reason ? ` — ${preview.tax_exempt_reason}` : ''}</span>
              </div>
            ) : (preview?.tax_breakdown || []).map((p, idx) => (
              <div key={idx} style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#64748b', marginBottom:4 }}>
                <span>{p.name} ({Number(p.rate)}%)</span><span>{fmt(p.amount)}</span>
              </div>
            ))}
            {preview?.tax_warning === 'no_tax_configured' && (
              <div data-testid="tax-warning" style={{ padding:'8px 10px', background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.35)', borderRadius:6, fontSize:11, color:'#fbbf24', margin:'6px 0', lineHeight:1.4 }}>⚠️ No tax configured — this quote will be saved with $0 tax. Set tax parts in Settings → Tax, or confirm "no sales tax" there.</div>
            )}
            {previewError && <div style={{ fontSize:11, color:'#f87171', margin:'6px 0' }}>⚠️ {previewError}</div>}
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:17, fontWeight:700, color:'#f97316', borderTop:'1px solid #2e3150', paddingTop:8, marginTop:4 }}>
              <span>Total</span><span>{preview ? fmt(total) : '—'}</span>
            </div>
          </div>
        </div>

        <div style={{ padding:'12px 24px', borderTop:'1px solid #2e3150', display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'9px 20px', borderRadius:8, border:'1px solid #3a3f60', background:'transparent', color:'#94a3b8', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={()=>saveQuote(false)} disabled={saving} style={{ padding:'9px 20px', borderRadius:8, border:'1px solid #3a3f60', background:'transparent', color:'#60a5fa', fontWeight:600, cursor:'pointer', fontSize:13 }}>
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button onClick={()=>saveQuote(true)} disabled={sending || (!form.client_email && !form.client_phone)} style={{ padding:'9px 20px', borderRadius:8, border:'none', background:'#f97316', color:'#fff', fontWeight:700, cursor:sending?'not-allowed':'pointer', fontSize:13, opacity:(!form.client_email&&!form.client_phone)?0.5:1 }}>
            {sending ? 'Sending...' : '📤 Save & Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Quotes Tab ───────────────────────────────────────────
export default function RentalQuotes({ inventoryItems = [] }) {
  const [quotes, setQuotes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm]           = useState(false);
  const [prefillRequest, setPrefillRequest] = useState(null);
  const [showSettings, setShowSettings]   = useState(false);
  const [flash, setFlash]   = useState('');

  function showFlash(msg) { setFlash(msg); setTimeout(()=>setFlash(''), 3000); }

  async function loadQuotes() {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/rentals/quotes`);
      setQuotes(data.quotes || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  useEffect(() => { loadQuotes(); }, []);

  async function sendQuote(id) {
    try {
      await axios.post(`/api/rentals/quotes/${id}/send`);
      showFlash('Quote sent!');
      loadQuotes();
    } catch(e) { alert('Send error: ' + (e.response?.data?.error || e.message)); }
  }

  async function deleteQuote(id) {
    if (!window.confirm('Delete this quote?')) return;
    await axios.delete(`/api/rentals/quotes/${id}`);
    loadQuotes();
  }

  function onSaved(quote, sent) {
    setShowForm(false);
    setPrefillRequest(null);
    showFlash(sent ? '✓ Quote saved and sent!' : '✓ Quote saved as draft');
    loadQuotes();
  }

  function buildFromRequest(req) {
    setPrefillRequest(req);
    setShowForm(true);
  }

  const S = {
    badge: (status) => ({ padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600, ...(STATUS_COLOR[status]||STATUS_COLOR.draft) }),
    th: { padding:'8px 12px', textAlign:'left', fontSize:11, color:'#4a5568', fontWeight:600, background:'#1e2235', borderBottom:'1px solid #2e3150', textTransform:'uppercase' },
    td: { padding:'10px 12px', borderBottom:'1px solid #1e2030', fontSize:13 },
  };

  return (
    <div>
      {flash && <div style={{ position:'fixed', top:16, right:16, background:'#14532d', color:'#4ade80', padding:'10px 20px', borderRadius:8, fontWeight:600, fontSize:13, zIndex:9999 }}>{flash}</div>}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontWeight:700, fontSize:18, color:'#e2e8f0' }}>Quotes</div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={()=>setShowSettings(true)} style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #3a3f60', background:'transparent', color:'#94a3b8', cursor:'pointer', fontSize:13 }}>⚙️ Mileage Settings</button>
          <button onClick={()=>setShowForm(true)} style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#f97316', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13 }}>+ New Quote</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'flex', gap:14, marginBottom:16 }}>
        {[
          { label:'Total', val: quotes.length, color:'#e2e8f0' },
          { label:'🔔 Requests', val: quotes.filter(q=>q.status==='pending_request').length, color:'#fbbf24' },
          { label:'Sent',  val: quotes.filter(q=>q.status==='sent'||q.status==='viewed').length, color:'#60a5fa' },
          { label:'Accepted', val: quotes.filter(q=>q.status==='accepted').length, color:'#4ade80' },
          { label:'Value', val: fmt(quotes.filter(q=>q.status!=='declined').reduce((s,q)=>s+Number(q.total),0)), color:'#f97316' },
        ].map(m => (
          <div key={m.label} style={{ flex:1, background:'#252838', border:'1px solid #2e3150', borderRadius:10, padding:'12px 16px' }}>
            <div style={{ fontSize:20, fontWeight:700, color:m.color }}>{m.val}</div>
            <div style={{ fontSize:11, color:'#4a5568', fontWeight:600, textTransform:'uppercase', letterSpacing:'.4px' }}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ background:'#252838', border:'1px solid #2e3150', borderRadius:10, overflow:'hidden' }}>
        {/* ── Pending Requests Banner ── */}
        {quotes.filter(q => q.status === 'pending_request').length > 0 && (
          <div style={{ marginBottom:16, background:'rgba(251,191,36,0.06)', border:'1px solid rgba(251,191,36,0.25)', borderRadius:10, overflow:'hidden' }}>
            <div style={{ padding:'10px 16px', background:'rgba(251,191,36,0.1)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#fbbf24' }}>🔔 {quotes.filter(q=>q.status==='pending_request').length} Pending Request{quotes.filter(q=>q.status==='pending_request').length>1?'s':''} — Action Required</div>
              <div style={{ fontSize:11, color:'#94a3b8' }}>Build and send a quote to respond</div>
            </div>
            {quotes.filter(q => q.status === 'pending_request').map(q => (
              <div key={q.id} style={{ padding:'12px 16px', borderTop:'1px solid rgba(251,191,36,0.1)', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap', marginBottom:4 }}>
                    <span style={{ fontSize:14, fontWeight:700, color:'#e2e8f0' }}>{q.client_name}</span>
                    {q.client_company && <span style={{ fontSize:12, color:'#f97316', fontWeight:600 }}>{q.client_company}</span>}
                    {q.client_email && <span style={{ fontSize:11, color:'#64748b' }}>{q.client_email}</span>}
                    {q.client_phone && <span style={{ fontSize:11, color:'#64748b' }}>{q.client_phone}</span>}
                  </div>
                  <div style={{ display:'flex', gap:14, flexWrap:'wrap', fontSize:12, color:'#64748b' }}>
                    {q.from_date && <span>📅 {fmtDate(q.from_date)} → {fmtDate(q.to_date)}</span>}
                    <span>{q.fulfillment_type === 'delivery' ? '🚚 Delivery' : '🏪 Pickup'}</span>
                    {q.delivery_address && <span>📍 {q.delivery_address}</span>}
                  </div>
                  {q.rental_quote_items?.length > 0 && (
                    <div style={{ marginTop:4, fontSize:12, color:'#94a3b8' }}>🔧 {q.rental_quote_items.map(i=>i.item_name).join(', ')}</div>
                  )}
                  {q.notes && <div style={{ marginTop:3, fontSize:12, color:'#4a5568', fontStyle:'italic' }}>💬 "{q.notes}"</div>}
                </div>
                <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                  <button onClick={() => buildFromRequest(q)}
                    style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#f97316', color:'#fff', fontWeight:700, fontSize:12, cursor:'pointer' }}>
                    📋 Build Quote
                  </button>
                  <button onClick={() => deleteQuote(q.id)}
                    style={{ padding:'8px 10px', borderRadius:8, border:'none', background:'#2a1a1a', color:'#f87171', fontSize:12, cursor:'pointer' }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={S.th}>#</th>
              <th style={S.th}>Client</th>
              <th style={S.th}>Items</th>
              <th style={S.th}>Rental Period</th>
              <th style={S.th}>Delivery</th>
              <th style={S.th}>Total</th>
              <th style={S.th}>Expires</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{...S.td, textAlign:'center', padding:40, color:'#4a5568'}}>Loading...</td></tr>}
            {!loading && quotes.length === 0 && (
              <tr><td colSpan={9} style={{...S.td, textAlign:'center', padding:40, color:'#3a3f60'}}>No quotes yet — create your first one</td></tr>
            )}
            {quotes.map(q => (
              <tr key={q.id} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={S.td}><span style={{ fontSize:10, color:'#4a5568' }}>#{q.id}</span></td>
                <td style={S.td}>
                  <div style={{ fontWeight:600, color:'#e2e8f0', fontSize:13 }}>{q.client_name}</div>
                  {q.client_company && <div style={{ fontSize:11, color:'#4a5568' }}>{q.client_company}</div>}
                </td>
                <td style={S.td}><span style={{ fontSize:12, color:'#94a3b8' }}>{(q.rental_quote_items||[]).length} item{(q.rental_quote_items||[]).length!==1?'s':''}</span></td>
                <td style={S.td}>
                  {q.from_date ? <span style={{ fontSize:11, color:'#64748b' }}>{fmtDate(q.from_date)} → {fmtDate(q.to_date)}</span> : <span style={{ color:'#3a3f60' }}>—</span>}
                </td>
                <td style={S.td}>
                  {q.fulfillment_type === 'pickup'
                    ? <span style={{ fontSize:11, color:'#4a5568' }}>Pickup</span>
                    : <span style={{ fontSize:11, color:'#94a3b8' }}>{q.delivery_miles > 0 ? `${q.delivery_miles}mi / ${fmt(q.mileage_charge||q.delivery_fee)}` : '—'}</span>
                  }
                </td>
                <td style={S.td}><span style={{ fontWeight:700, color:'#f97316' }}>{fmt(q.total)}</span></td>
                <td style={S.td}><span style={{ fontSize:11, color: q.expiry_date && new Date(q.expiry_date) < new Date() ? '#f87171' : '#64748b' }}>{fmtDate(q.expiry_date)}</span></td>
                <td style={S.td}><span style={S.badge(q.status)}>{q.status}</span></td>
                <td style={S.td}>
                  <div style={{ display:'flex', gap:6 }}>
                    {(q.status === 'draft' || q.status === 'sent') && (q.client_email || q.client_phone) && (
                      <button onClick={()=>sendQuote(q.id)} style={{ padding:'4px 10px', borderRadius:6, border:'none', background:'#1e3a5f', color:'#60a5fa', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                        {q.status === 'sent' ? 'Resend' : 'Send'}
                      </button>
                    )}
                    {q.status === 'accepted' && q.converted_order_id && (
                      <span style={{ fontSize:11, color:'#4ade80' }}>→ Order #{q.converted_order_id}</span>
                    )}
                    <button onClick={()=>deleteQuote(q.id)} style={{ padding:'4px 8px', borderRadius:6, border:'none', background:'#2a1a1a', color:'#f87171', fontSize:11, cursor:'pointer' }}>✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showSettings && <MileageSettings onClose={()=>setShowSettings(false)} />}
      {showForm && <QuoteForm onClose={()=>{ setShowForm(false); setPrefillRequest(null); }} onSaved={onSaved} inventoryItems={inventoryItems} prefill={prefillRequest} />}
    </div>
  );
}
